import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import BuilderStartScreen from './BuilderStartScreen';
import BuilderPromptScreen from './BuilderPromptScreen';
import BuilderWorkspace from './BuilderWorkspace';
import BuilderSidebar from './BuilderSidebar';
import BuilderProjectsGrid from './BuilderProjectsGrid';
import SessionBootScreen from './SessionBootScreen';
import { useProjectStore } from '../../stores/projectStore';
import { useSession } from '../../context/SessionContext';
import { cloudProjectApi } from '../../services/cloudApi';
import { getAuthToken } from '../../utils/auth';
import { formatErrorMessage } from '../../utils/errorUtils';
import { useAgentConfigStore } from '../../stores/agentConfigStore';
import {
  peekThreediumDeepLink,
  setCreatedProjectId,
  consumeThreediumDeepLink,
  isSupportedHandoff,
} from '../../utils/threediumDeepLink';

// Builder flow states
const BUILDER_STATES = {
  START: 'start',
  PROJECTS: 'projects',
  PROMPT: 'prompt',
  WORKSPACE: 'workspace',
  BOOTING: 'booting',
};

function openExperience(navigate, projectId, { replace = false, initialPrompt } = {}) {
  if (!projectId) return;
  const options = {};
  if (replace) options.replace = true;
  if (initialPrompt) options.state = { initialPrompt };
  navigate(`/experience/${projectId}`, options);
}

function BuilderPage({ onStateChange, chatSidebarOpen = true, viewMode = 'preview', deviceMode = 'desktop', initialProjectId, onProjectChange }) {
  // Navigation
  const navigate = useNavigate();
  const location = useLocation();

  // Session context (cloud mode)
  const { isReady: sessionReady, sessionStatus, error: sessionError, retrySession, isCloudMode } = useSession();

  // Project store
  const {
    currentProject,
    projects,
    loadProjects,
    createProject,
    setCurrentProject,
    upsertListedProject,
    clearMessages,
    stopAllVite,
  } = useProjectStore();

  const [builderState, setBuilderState] = useState(null); // null = loading
  const [hasInitialized, setHasInitialized] = useState(false);
  const [hasHandledInitialProject, setHasHandledInitialProject] = useState(false);
  const [selectedExperience, setSelectedExperience] = useState(null);
  const [currentPrompt, setCurrentPrompt] = useState('');
  const [activeChat, setActiveChat] = useState(null);

  // Pending action queued while session boots (cloud mode only)
  const [pendingAction, setPendingAction] = useState(null);
  const [localError, setLocalError] = useState(null);
  const pendingActionRef = useRef(null);
  pendingActionRef.current = pendingAction;
  const threediumDeepLinkHandledRef = useRef(false);
  // Set synchronously before setBuilderState(BOOTING) so the sibling
  // "once projects loaded" effect (same flush, stale builderState === null)
  // does not overwrite BOOTING with PROJECTS/START.
  const threediumDeepLinkActiveRef = useRef(false);

  // Load projects on mount and set initial state
  useEffect(() => {
    const initializeProjects = async () => {
      await loadProjects();
      setHasInitialized(true);
    };
    initializeProjects();
  }, []);

  // Homepage handoff: create the AWS project on /dashboard, then leave.
  // MainAppGate remounts on /experience/:id — do not enter WORKSPACE here.
  useEffect(() => {
    if (!hasInitialized || initialProjectId || threediumDeepLinkHandledRef.current) return;

    const payload = peekThreediumDeepLink();
    if (!payload || !isSupportedHandoff(payload)) return;

    threediumDeepLinkHandledRef.current = true;

    if (payload.createdProjectId) {
      threediumDeepLinkActiveRef.current = true;
      setBuilderState(BUILDER_STATES.BOOTING);
      navigate(`/experience/${payload.createdProjectId}`, { replace: true });
      return;
    }

    threediumDeepLinkActiveRef.current = true;
    setBuilderState(BUILDER_STATES.BOOTING);

    const runDeepLink = async () => {
      try {
        await useAgentConfigStore.getState().initialize();
        await useAgentConfigStore.getState().loadDefaultForTemplate('r3f');

        const prompt = (payload.inputText || '').trim();
        const projectName = prompt.substring(0, 30).replace(/[^a-zA-Z0-9 ]/g, '') || 'New Project';
        const description = prompt ? prompt.substring(0, 100) : 'Created from Threedium homepage';

        const awsProject = await cloudProjectApi.createFromTemplate({
          name: projectName,
          template: 'r3f',
          description,
        });
        setCreatedProjectId(awsProject.id);
        upsertListedProject(awsProject);
        navigate(`/experience/${awsProject.id}`, { replace: true });
      } catch (error) {
        console.error('Failed to run Threedium deep-link:', error);
        threediumDeepLinkActiveRef.current = false;
        setLocalError(error.message || 'Failed to create project from homepage prompt.');
        setBuilderState(projects.length > 0 ? BUILDER_STATES.PROJECTS : BUILDER_STATES.START);
      }
    };

    runDeepLink();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasInitialized, initialProjectId]);

  // Handle initial project ID from URL (e.g., /experience/:projectId)
  useEffect(() => {
    if (hasInitialized && initialProjectId && !hasHandledInitialProject) {
      setHasHandledInitialProject(true);

      const payload = peekThreediumDeepLink();
      const isHandoff = Boolean(
        payload
        && isSupportedHandoff(payload)
        && String(payload.createdProjectId) === String(initialProjectId)
      );
      const handoffPrompt = isHandoff ? (payload.inputText || '').trim() : '';
      const navigationPrompt = typeof location.state?.initialPrompt === 'string'
        ? location.state.initialPrompt.trim()
        : '';
      const promptToUse = handoffPrompt || navigationPrompt;
      if (location.state?.initialPrompt) {
        navigate(location.pathname, { replace: true, state: {} });
      }

      if (isCloudMode && !sessionReady) {
        setPendingAction({
          type: 'open',
          projectId: initialProjectId,
          prompt: promptToUse || null,
          handoff: isHandoff,
        });
        setBuilderState(BUILDER_STATES.BOOTING);
        return;
      }

      const loadInitialProject = async () => {
        try {
          if (isHandoff) {
            await useAgentConfigStore.getState().initialize();
            await useAgentConfigStore.getState().loadDefaultForTemplate('r3f');
          }
          const loadedProject = await setCurrentProject(initialProjectId);
          const project = loadedProject || useProjectStore.getState().currentProject;
          setSelectedExperience({
            id: project?.aws_project_id || initialProjectId,
            name: project?.display_name || project?.name || 'Project',
            projectId: project?.aws_project_id || initialProjectId,
          });
          if (isHandoff && !promptToUse) {
            consumeThreediumDeepLink();
          }
          setCurrentPrompt(promptToUse);
          setBuilderState(BUILDER_STATES.WORKSPACE);
        } catch (error) {
          console.error('Failed to load project from URL:', error);
          navigate('/dashboard', { replace: true });
        }
      };
      loadInitialProject();
    }
  }, [hasInitialized, initialProjectId, hasHandledInitialProject, projects, setCurrentProject, isCloudMode, sessionReady]);

  // Once projects are loaded, set the appropriate initial state (only if no initial project to load)
  useEffect(() => {
    if (hasInitialized && !initialProjectId) {
      if (builderState === null && !threediumDeepLinkActiveRef.current) {
        setBuilderState(projects.length > 0 ? BUILDER_STATES.PROJECTS : BUILDER_STATES.START);
      }
    }
  }, [projects, hasInitialized, builderState, initialProjectId]);

  // Back to dashboard: refetch the project list. Creating a project (handoff or
  // cloud boot) often never updates the in-memory list, so without this the
  // first project is missing until a full page refresh.
  useEffect(() => {
    if (!hasInitialized || initialProjectId || location.pathname !== '/dashboard') return;
    if (threediumDeepLinkActiveRef.current) return;
    const handoff = peekThreediumDeepLink();
    if (handoff && isSupportedHandoff(handoff) && !handoff.createdProjectId) return;

    loadProjects().catch(() => {
      // Keep whatever list we already have
    });
  }, [location.pathname, initialProjectId, hasInitialized]); // eslint-disable-line react-hooks/exhaustive-deps

  // ============================================================
  // Boot completion: session became ready while we have a pending action
  // ============================================================
  useEffect(() => {
    if (!sessionReady || builderState !== BUILDER_STATES.BOOTING || !pendingActionRef.current) return;

    const action = pendingActionRef.current;
    const completeBoot = async () => {
      try {
        if (action.type === 'new') {
          // AWS project already created — now clone to session backend
          const awsProjectId = action.awsProject.id;
          const authToken = getAuthToken();
          const { projectApi } = await import('../../services/api');
          const cloneResult = await projectApi.cloneFromAws(awsProjectId, authToken);
          const project = {
            ...cloneResult.project,
            aws_project_id: awsProjectId,
            awsProject: action.awsProject,
          };

          // Update the store
          useProjectStore.setState(state => ({
            projects: [...state.projects, project],
            currentProject: project,
            isLoading: false,
          }));

          // Load files and start Vite
          await useProjectStore.getState().loadFiles();
          await useProjectStore.getState().startVite().catch(err => {
            console.error('Auto-start Vite failed:', err);
          });

          setSelectedExperience({
            id: awsProjectId,
            name: action.awsProject.name || project.name,
            projectId: awsProjectId,
            solutionId: action.solutionId || null,
          });

          setCurrentPrompt(action.prompt || '');
          setBuilderState(BUILDER_STATES.WORKSPACE);
          if (location.pathname !== `/experience/${awsProjectId}`) {
            openExperience(navigate, awsProjectId, { initialPrompt: action.prompt || undefined });
          }

        } else if (action.type === 'open') {
          if (action.handoff) {
            await useAgentConfigStore.getState().initialize();
            await useAgentConfigStore.getState().loadDefaultForTemplate('r3f');
          }
          const loadedProject = await setCurrentProject(action.projectId);
          const project = loadedProject || useProjectStore.getState().currentProject;
          const openedId = project?.aws_project_id || action.projectId;
          setSelectedExperience({
            id: openedId,
            name: project?.display_name || project?.name || 'Project',
            projectId: openedId,
          });
          if (action.handoff && !action.prompt) {
            consumeThreediumDeepLink();
          }
          setCurrentPrompt(action.prompt || '');
          setBuilderState(BUILDER_STATES.WORKSPACE);
          if (location.pathname !== `/experience/${openedId}`) {
            openExperience(navigate, openedId, { initialPrompt: action.prompt || undefined });
          }
        }
      } catch (error) {
        console.error('Failed to complete boot action:', error);
        if (initialProjectId) {
          navigate('/dashboard', { replace: true });
        } else {
          setBuilderState(projects.length > 0 ? BUILDER_STATES.PROJECTS : BUILDER_STATES.START);
        }
      } finally {
        setPendingAction(null);
      }
    };

    completeBoot();
  }, [sessionReady, builderState]); // eslint-disable-line react-hooks/exhaustive-deps

  // Session died mid-workspace — return to the boot screen (Designer remounts it automatically).
  useEffect(() => {
    if (!isCloudMode || sessionReady || builderState !== BUILDER_STATES.WORKSPACE) return;
    const projectId =
      selectedExperience?.projectId ||
      selectedExperience?.id ||
      currentProject?.aws_project_id;
    if (projectId) {
      const payload = peekThreediumDeepLink();
      const isHandoff = Boolean(
        payload
        && isSupportedHandoff(payload)
        && String(payload.createdProjectId) === String(projectId)
      );
      setPendingAction({
        type: 'open',
        projectId,
        prompt: isHandoff ? (payload.inputText || '').trim() || null : undefined,
        handoff: isHandoff,
      });
    }
    setBuilderState(BUILDER_STATES.BOOTING);
  }, [isCloudMode, sessionReady, builderState, selectedExperience, currentProject]);

  // After a session-loss boot, if there is nothing queued, leave the boot screen when ready.
  useEffect(() => {
    if (!isCloudMode || !sessionReady || builderState !== BUILDER_STATES.BOOTING) return;
    if (pendingActionRef.current) return;
    if (threediumDeepLinkActiveRef.current) return;
    if (initialProjectId) return;
    setBuilderState(projects.length > 0 ? BUILDER_STATES.PROJECTS : BUILDER_STATES.START);
  }, [isCloudMode, sessionReady, builderState, projects.length, initialProjectId]);

  // Notify parent of state changes
  useEffect(() => {
    if (onStateChange) {
      onStateChange(builderState);
    }
  }, [builderState, onStateChange]);

  // Notify parent of project changes
  useEffect(() => {
    if (onProjectChange) {
      onProjectChange(currentProject || selectedExperience);
    }
  }, [currentProject, selectedExperience, onProjectChange]);

  /**
   * Handle project selection (from existing projects)
   */
  const handleSelectExperience = (experience) => {
    if (experience.projectId) {
      openExperience(navigate, experience.projectId);
    }
  };

  /**
   * Handle creating new project from template.
   * In cloud mode: if session not ready, create on AWS immediately (non-blocking)
   * and show boot screen. The clone happens when session is ready.
   */
  const handleSelectTemplate = async (template = 'r3f', options = {}) => {
    setLocalError(null);
    try {
      const { solutionId, prompt } = options;
      const projectName = solutionId
        ? `Workspace ${solutionId}`
        : 'New Project';
      const description = solutionId
        ? `Threedium workspace for solution ${solutionId}`
        : 'Created from Builder';

      if (isCloudMode && !sessionReady) {
        // Non-blocking: create on AWS (doesn't need session backend), then open.
        // The experience route remounts and clones once the session is ready.
        const awsProject = await cloudProjectApi.createFromTemplate({
          name: projectName,
          template,
          description,
          solution_id: solutionId,
        });
        upsertListedProject(awsProject);
        openExperience(navigate, awsProject.id, { initialPrompt: prompt || undefined });
        return;
      }

      // Fast path: session already running
      const project = await createProject(projectName, template, description, solutionId);
      openExperience(navigate, project.aws_project_id || project.id, { initialPrompt: prompt || undefined });
    } catch (error) {
      console.error('Failed to create project:', error);
      setLocalError(error.message || 'Failed to create project. Please try again.');
      if (isCloudMode) {
        setBuilderState(BUILDER_STATES.BOOTING);
      }
    }
  };

  /**
   * Handle prompt submission - create project if needed
   */
  const handleSubmitPrompt = async (prompt) => {
    if (!currentProject) {
      setLocalError(null);
      try {
        const projectName = prompt.substring(0, 30).replace(/[^a-zA-Z0-9 ]/g, '') || 'New Project';

        if (isCloudMode && !sessionReady) {
          const awsProject = await cloudProjectApi.createFromTemplate({
            name: projectName,
            template: 'r3f',
            description: prompt.substring(0, 100),
          });
          upsertListedProject(awsProject);
          openExperience(navigate, awsProject.id, { initialPrompt: prompt });
          return;
        }

        const project = await createProject(projectName, 'r3f', prompt.substring(0, 100));
        openExperience(navigate, project.aws_project_id || project.id, { initialPrompt: prompt });
        return;
      } catch (error) {
        console.error('Failed to create project:', error);
        setLocalError(error.message || 'Failed to create project. Please try again.');
        if (isCloudMode) {
          setBuilderState(BUILDER_STATES.BOOTING);
        }
        return;
      }
    }

    setCurrentPrompt(prompt);
    setBuilderState(BUILDER_STATES.WORKSPACE);
  };

  /**
   * Handle new chat / reset
   */
  const handleNewChat = () => {
    if (sessionReady) stopAllVite();
    setBuilderState(BUILDER_STATES.START);
    setSelectedExperience(null);
    setCurrentPrompt('');
    setActiveChat(null);
    setPendingAction(null);
    setLocalError(null);
    clearMessages();
    if (location.pathname !== '/dashboard') {
      navigate('/dashboard');
    }
  };

  /**
   * Handle going back to projects list
   */
  const handleBackToProjects = async () => {
    consumeThreediumDeepLink();
    if (sessionReady) stopAllVite();
    setSelectedExperience(null);
    setCurrentPrompt('');
    setPendingAction(null);
    setLocalError(null);
    setActiveChat(null);
    try {
      await loadProjects();
    } catch {
      // fall through with current list
    }
    const count = useProjectStore.getState().projects.length;
    setBuilderState(count > 0 ? BUILDER_STATES.PROJECTS : BUILDER_STATES.START);
    if (location.pathname !== '/dashboard') {
      navigate('/dashboard');
    }
  };

  /**
   * Handle selecting existing chat/project from sidebar
   */
  const handleSelectChat = async (chat) => {
    setActiveChat(chat);

    const projectId = chat.projectId || chat.id;
    if (projectId) {
      openExperience(navigate, projectId);
      return;
    }

    setSelectedExperience({ id: chat.experienceId, name: chat.title });
    setCurrentPrompt('');
    setBuilderState(BUILDER_STATES.WORKSPACE);
  };

  const handleBootRetry = useCallback(() => {
    if (!isCloudMode) return;
    if (retrySession) retrySession();
  }, [retrySession, isCloudMode]);

  const renderContent = () => {
    // Show loading state while determining initial view
    if (builderState === null) {
      return (
        <div className="builder-loading-screen">
          <div className="builder-loading-spinner" />
        </div>
      );
    }

    switch (builderState) {
      case BUILDER_STATES.BOOTING:
        return (
          <SessionBootScreen
            sessionStatus={sessionStatus}
            error={localError || sessionError}
            onRetry={() => {
              setLocalError(null);
              handleBootRetry();
            }}
            pendingAction={pendingAction}
          />
        );
      case BUILDER_STATES.PROJECTS:
        return (
          <BuilderProjectsGrid
            onSelectProject={(project) => {
              const projectId = project.aws_project_id || project.id;
              openExperience(navigate, projectId);
            }}
            onCreateNew={handleNewChat}
          />
        );
      case BUILDER_STATES.START:
        return (
          <BuilderStartScreen
            onSelectExperience={handleSelectExperience}
            onSelectTemplate={handleSelectTemplate}
          />
        );
      case BUILDER_STATES.PROMPT:
        return (
          <BuilderPromptScreen
            experience={selectedExperience}
            onSubmitPrompt={handleSubmitPrompt}
            onBack={handleBackToProjects}
          />
        );
      case BUILDER_STATES.WORKSPACE:
        return (
          <BuilderWorkspace
            experience={selectedExperience}
            initialPrompt={currentPrompt}
            onNewChat={handleNewChat}
            chatSidebarOpen={chatSidebarOpen}
            viewMode={viewMode}
            deviceMode={deviceMode}
          />
        );
      default:
        return null;
    }
  };

  // Determine if we should show the sidebar
  const showSidebar = builderState !== null &&
                      builderState !== BUILDER_STATES.WORKSPACE &&
                      builderState !== BUILDER_STATES.BOOTING;

  return (
    <div className={`builder-page ${builderState === 'workspace' ? 'workspace-mode' : 'content-mode'}`}>
      {showSidebar && (
        <BuilderSidebar
          onNewChat={handleNewChat}
          onBackToProjects={projects.length > 0 ? handleBackToProjects : null}
        />
      )}
      <div className="builder-main">
        {localError && builderState !== BUILDER_STATES.BOOTING && (
          <div className="builder-error-banner" onClick={() => setLocalError(null)}>
            <span>⚠️ {formatErrorMessage(localError)}</span>
            <button type="button">✕</button>
          </div>
        )}
        {renderContent()}
      </div>
    </div>
  );
}

export default BuilderPage;
